import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ControllerListarSegurosClinica, ContadoresSeguros } from "../../Service/Services.seguros";
import { GetTokenDecoded } from "../../utils/storeUsuario";
import { usePageSubtitulo } from "../../context/PageHeaderContext";
import DataTable from "../../Components/UI/DataTable/DataTable";
import { CampoTexto } from "../../Components/UI/Campo";
import { ChipEstado } from "../../Components/UI/Chip";
import { IconoBuscar } from "../../Components/UI/Iconos";
import Pagina from "./Pagina";
import "../../styles/Seguros.css";

/* ============================================================================
   SEGUROS-PLANES FASE 2 · lista de aseguradoras.

   Es el paso 1 del flujo nuevo: se elige el seguro UNA vez y a partir de ahí
   viaja en la URL. Cada fila lleva a su ficha.

   Los contadores salen de `GET /seguros/contadores` (P2b): una sola petición
   para toda la clínica. La alternativa era tres peticiones por seguro —69 en
   total— para pintar tres números por fila.

   Los contadores llegan por separado de la lista y a propósito NO la bloquean:
   la tabla se pinta en cuanto hay seguros, y los números aparecen cuando
   llegan. Si la petición de contadores falla, se ven guiones y la pantalla
   sigue siendo perfectamente utilizable.
   ========================================================================== */

// "Todavía no lo sé" y "no tiene ninguno" no son lo mismo, y un 0 prematuro
// haría creer que una aseguradora no tiene tarifario.
function Numero({ valor }) {
    if (!Number.isFinite(valor)) return <span className="sg-dato__valor--vacio">—</span>;
    return <span style={{ fontVariantNumeric: "tabular-nums" }}>{valor}</span>;
}

export default function SegurosLista() {
    const navigate = useNavigate();
    const [seguros, setSeguros] = useState([]);
    const [contadores, setContadores] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState(null);
    const [busqueda, setBusqueda] = useState("");

    const cargar = useCallback(async () => {
        setCargando(true);
        setError(null);
        try {
            const idclinica = GetTokenDecoded().idclinica;
            const lista = await ControllerListarSegurosClinica(idclinica);
            if (!Array.isArray(lista)) throw new Error("respuesta inesperada");
            setSeguros(lista);
            setCargando(false);

            // Deliberadamente DESPUÉS y sin await bloqueante: los contadores son
            // accesorios y no deben retrasar la tabla.
            ContadoresSeguros(idclinica).then((filas) => {
                const mapa = new Map((filas || []).map((f) => [Number(f.idseguro), f]));
                setContadores(mapa);
            });
        } catch (e) {
            setError("No se pudieron cargar las aseguradoras.");
            setCargando(false);
        }
    }, []);

    useEffect(() => {
        cargar();
    }, [cargar]);

    const filas = useMemo(() => {
        const texto = busqueda.trim().toLocaleLowerCase("es");
        return seguros
            .filter((s) => {
                if (!texto) return true;
                return (
                    String(s.nombre || "").toLocaleLowerCase("es").includes(texto) ||
                    String(s.ruc || "").toLocaleLowerCase("es").includes(texto) ||
                    String(s.razon_social || "").toLocaleLowerCase("es").includes(texto)
                );
            })
            .map((s) => {
                const c = contadores?.get(Number(s.idseguro));
                return {
                    idseguro: s.idseguro,
                    nombre: s.nombre,
                    tipo: s.tipoSeguro?.tipo || "",
                    estado: s.estado,
                    procedimientos: c?.procedimientos,
                    planes: c?.planes,
                    promociones: c?.promociones,
                };
            });
    }, [seguros, contadores, busqueda]);

    usePageSubtitulo(cargando ? "" : `${seguros.length} aseguradora${seguros.length === 1 ? "" : "s"}`);

    const columnas = [
        {
            key: "nombre",
            titulo: "Aseguradora",
            render: (f) => (
                <span style={{ fontWeight: 500 }}>{f.nombre}</span>
            ),
        },
        { key: "tipo", titulo: "Tipo" },
        {
            key: "procedimientos",
            titulo: "Procedimientos",
            alinear: "derecha",
            ancho: 130,
            render: (f) => <Numero valor={f.procedimientos} />,
        },
        {
            key: "planes",
            titulo: "Planes",
            alinear: "derecha",
            ancho: 90,
            render: (f) => <Numero valor={f.planes} />,
        },
        {
            key: "promociones",
            titulo: "Promociones",
            alinear: "derecha",
            ancho: 115,
            render: (f) => <Numero valor={f.promociones} />,
        },
        {
            key: "estado",
            titulo: "Estado",
            ancho: 110,
            render: (f) => (
                <ChipEstado tono={f.estado === "A" ? "hecho" : "anulado"}>
                    {f.estado === "A" ? "Activo" : "Inactivo"}
                </ChipEstado>
            ),
        },
    ];

    return (
        <Pagina>
            <div style={{ maxWidth: 360, marginBottom: 14 }}>
                <CampoTexto
                    etiqueta="Buscar aseguradora"
                    etiquetaOculta
                    icono={IconoBuscar}
                    placeholder="Buscar por nombre, RUC o razón social"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    aria-label="Buscar aseguradora"
                />
            </div>

            <DataTable
                columnas={columnas}
                filas={filas}
                filaKey="idseguro"
                cargando={cargando}
                error={error}
                onReintentar={cargar}
                onFilaClick={(f) => navigate(`/configuracion/seguros/${f.idseguro}`)}
                hayFiltrosAplicados={Boolean(busqueda.trim())}
                onLimpiarFiltros={() => setBusqueda("")}
                textoVacioSinFiltros="Todavía no hay aseguradoras registradas."
                etiquetaTabla="Aseguradoras"
            />
        </Pagina>
    );
}
